import React, { useState, useEffect, useRef } from 'react';
import Note from './Note';
import { db, auth } from '../firebaseConfig';

// Extract the function outside the component so it can be exported
// This function fetches similar notes based on a given note ID and a similarity threshold
export const findSimilarNotes = async (noteId, userId) => {
  if (!noteId) {
    console.error('Missing noteId parameter in findSimilarNotes');
    return [];
  }
  
  if (!userId) {
    console.error('Missing userId parameter in findSimilarNotes');
    return [];
  }
  
  try {
    // Call Pinecone API to get similar notes
    const response = await fetch('/api/similarNotes', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        noteId: noteId,
        uid: userId // Use the userId parameter directly instead of looking it up
      }),
    });
    
    // Check if the response is okay
    if (!response.ok) {
      throw new Error('Failed to fetch similar notes from Pinecone');
    }
    
    const result = await response.json();
    
    if (!result.matches || !Array.isArray(result.matches)) {
      console.error('Invalid response format - matches property missing or not an array:', result);
      return [];
    }
    
    // Map the response to a more usable format
    const mappedNotes = result.matches.map(match => {
      return {
        // Use metadata if you store complete note there
        ...match.metadata,
        id: match.id,
        // Try different potential text property locations
        text: match.metadata?.text || match.metadata?.content || match.text || '',
        similarity: match.score
      };
    }).filter(note => note.text && note.uid === userId); // Filter based on text, similarity, and user ID
    
    return mappedNotes; // Return the filtered notes
  } catch (error) {
    console.error('Error fetching similar notes:', error);
    return []; // Return empty array on error
  }
};

// SimilarNotes component to display similar notes
function SimilarNotes({ 
  activeNoteId,
  similarityMatrix,
  notes,
  similarityThreshold,
  setSimilarityThreshold,
  similarNotes,
  setSimilarNotes,
  onFindSimilar,
  onClose
}) {
  const [nestedPanels, setNestedPanels] = useState([]); // State to manage nested panels
  const containerRef = useRef(null); // Reference for the container
  const [panelWidth, setPanelWidth] = useState(440); // State for panel width
  const [isLoading, setIsLoading] = useState(false); // Add local loading state

  // Calculate panel width based on window size
  useEffect(() => {
    const calculatePanelWidth = () => {
      if (!containerRef.current) return;
      const availableWidth = window.innerWidth - 16; // window width minus 16px
      // On mobile or narrow screens, each panel takes full width minus padding
      const calculatedWidth = Math.min(440, availableWidth);
      setPanelWidth(calculatedWidth); // Set the calculated width
    };

    calculatePanelWidth(); // Initial calculation
    window.addEventListener('resize', calculatePanelWidth); // Update on resize
    return () => window.removeEventListener('resize', calculatePanelWidth); // Cleanup on unmount
  }, []);

  // Add a new panel to the stack of nested panels
  const addPanel = (noteId, similarNotes) => {
    setNestedPanels(prev => [...prev, { noteId, similarNotes }]);
  };

  // Handle click on a panel to show only up to that index
  const handlePanelClick = (index) => {
    setNestedPanels(prev => prev.slice(0, index + 1));
  };

  // Close specific panel and all panels after it
  const handleClosePanel = (index) => {
    if (index === 0) {
      // Close the entire similarity panel
      onClose();
    } else {
      setNestedPanels(prev => prev.slice(0, index)); // Close panels after the clicked index
    }
  };

  // Clear panels when active note changes
  useEffect(() => {
    setNestedPanels([]); // Reset nested panels
  }, [activeNoteId]);

  // Render a panel with similar notes
  const renderPanel = (panelSimilarNotes, index) => {
    return (
      <div 
        key={index} 
        className="flex-none bg-white border-r border-gray-200 overflow-y-auto max-h-screen"
        style={{ 
          width: `${panelWidth}px`, // Set panel width
          paddingBottom: 'var(--app-bar-height, 120px)' // Padding for app bar
        }}
      >
        <div className="flex justify-between items-center border-b border-gray-100">
          <h3 className="text-xs px-6 py-3 font-semibold uppercase text-gray-400">
            Similar Notes {index > 0 ? `(${index + 1})` : ''} 
          </h3>
          <button
            onClick={() => handleClosePanel(index)} // Close panel on button click
            className="px-6 py-3 text-gray-400 hover:text-gray-600"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {Array.isArray(panelSimilarNotes) && panelSimilarNotes.length > 0 ? (
          <div>
            {panelSimilarNotes.map(note => (
              <Note 
                key={note.id}
                note={{
                  ...note,
                  id: note.id,
                  text: note.text || note.content || '',
                  content: note.content || note.text || '',
                }}
                onNoteChange={() => {}} // Placeholder for note change handler
                onFindSimilar={(noteId) => {
                  // Fetch similar notes for the clicked note
                  findSimilarNotes(noteId, auth.currentUser.uid)
                    .then(newSimilarNotes => {
                      addPanel(noteId, newSimilarNotes); // Add new panel with similar notes
                    });
                }}
                similarNotes={[]} // Placeholder for similar notes
                onActive={() => handlePanelClick(index)} // Handle panel activation
              />
            ))}
          </div>
        ) : (
          <div className="px-6 py-4 text-gray-600">
            No notes found
          </div>
        )}
      </div>
    );
  };

  // Fetch similar notes on initial render when activeNoteId changes
  useEffect(() => {
    if (activeNoteId && auth.currentUser?.uid) {
      setIsLoading(true); // Set loading to true
      findSimilarNotes(activeNoteId, auth.currentUser.uid)
        .then(newSimilarNotes => {
          setSimilarNotes(newSimilarNotes);
          setIsLoading(false); // Set loading to false when done
        })
        .catch(error => {
          console.error('Error fetching initial similar notes:', error);
          setIsLoading(false); // Set loading to false on error
        });
    }
  }, [activeNoteId]);

  return (
    <div ref={containerRef} className="flex overflow-x-auto">
      {isLoading ? (
        <div className="flex-none bg-white border-r border-gray-200 overflow-y-auto max-h-screen"
             style={{ width: `${panelWidth}px`, paddingBottom: 'var(--app-bar-height, 120px)' }}>
          <div className="px-6 py-4 text-gray-600">
            Loading similar notes...
          </div>
        </div>
      ) : (
        <>
          {renderPanel(similarNotes, 0)}
          {nestedPanels.map((panel, index) => 
            renderPanel(panel.similarNotes, index + 1)
          )}
        </>
      )}
    </div>
  );
}

export default SimilarNotes; 